import { ref } from 'vue'
import { experiment } from '@amplitude/unified'
import accountStore from '@/stores/account.store'

type ExperimentClient = NonNullable<ReturnType<typeof experiment>>

class AmplitudeExperimentService {
  private client: ExperimentClient | null = null

  /**
   * Reactive readiness signal. Flips to `true` once the experiment state is
   * settled — variants fetched, or we know none are coming (no deployment key,
   * or the fetch failed). Settling happens on the first consent interaction,
   * whichever way it resolves.
   *
   * Consumers read this (directly or via {@link variant}) so their computeds
   * re-evaluate once assignments arrive. This avoids the race where the UI is
   * built against the fallback (control) simply because the fetch had not yet
   * returned — the Experiment fetch runs behind the consent flow and finishes
   * seconds after the first onboarding / mainscreen evaluation.
   */
  private readonly ready = ref(false)

  private resolveReady!: () => void
  private readonly readyPromise = new Promise<void>((resolve) => {
    this.resolveReady = resolve
  })

  private markReady(): void {
    if (this.ready.value) return
    this.ready.value = true
    this.resolveReady()
  }

  /**
   * Resolves once the experiment state is settled, or after `timeoutMs` as a
   * fail-safe so callers never hang (they then fall back to control).
   */
  public whenReady(timeoutMs = 4000): Promise<void> {
    if (this.ready.value) return Promise.resolve()
    return Promise.race([
      this.readyPromise,
      new Promise<void>((resolve) => setTimeout(resolve, timeoutMs)),
    ])
  }

  /**
   * Fetches flag variants for the current user. Must be called after the Unified
   * SDK is initialized and the user id is set ({@link setUserId}).
   *
   * The Experiment plugin shares the Analytics identity via the analytics
   * connector, so exposure/assignment events land on the same Amplitude profile
   * as behaviour events. We pass the same hashed user id that Analytics uses, so
   * the experiment user is guaranteed identical to the analytics/recording user.
   */
  public async initialize(): Promise<void> {
    const deploymentKey = import.meta.env
      .VITE_AMPLITUDE_EXPERIMENT_DEPLOYMENT_KEY

    if (!deploymentKey) {
      console.warn('AmplitudeExperimentService: No deployment key configured')
      this.markReady()
      return
    }

    try {
      const client = experiment()
      if (!client) {
        console.warn(
          'AmplitudeExperimentService: Experiment client unavailable',
        )
        return
      }

      this.client = client

      const userId = accountStore.state.account?.amplitudeUserId
      await client.fetch(userId ? { user_id: userId } : undefined)
    } catch (e) {
      console.warn('AmplitudeExperimentService: Initialization failed', e)
    } finally {
      this.markReady()
    }
  }

  /**
   * Clears the cached experiment client so subsequent {@link variant} calls fall
   * back to control. Not part of the consent flow — with the Unified SDK,
   * analytics (and therefore the experiment identity) keep running after consent
   * is revoked. Kept as an explicit reset hook (e.g. for tests).
   */
  public teardown(): void {
    this.client = null
    this.markReady()
  }

  /**
   * Returns the assigned variant value for a given flag key.
   * Returns null if the client is not initialized or no variant is assigned.
   */
  public variant(flagKey: string): string | null {
    // Reading `ready` registers a reactive dependency so computed consumers
    // re-evaluate once variants are fetched. Until settled we return null so
    // callers use the control fallback rather than a not-yet-fetched value.
    if (!this.ready.value) return null
    return this.client?.variant(flagKey)?.value ?? null
  }
}

export default new AmplitudeExperimentService()
